'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { ENV } from '@/app/config/env';

import { VtMark } from '../components/brand';
import { RatingStars, TP_STAR_PATH } from '../components/rating-stars';
import { streamDelay, streamedChunks, typingSteps } from '../lib/llm-type';
import { asset, markPiece } from '../lib/nav';
import {
  leadFormPostHogIds,
  trackLeadFormShown,
  trackLeadFormStepCompleted,
  trackLeadFormStepViewed,
  trackLeadFormSubmitAttempted,
  type InputMethod,
  type LeadFormStep,
} from '@/app/lib/lead-form-analytics';
import { fetchDistrictsForZip } from './districts';
import {
  CONTACT_KEYS,
  ROADMAP,
  buildPages,
  ctxFor,
  echoFor,
  flagsFrom,
  isContact,
  leadAnswersFrom,
  newState,
  optionsFor,
  parseState,
  withAnswer,
  type Answer,
  type ChatForm,
  type ChatState,
  type Contact,
  type Ctx,
  type DistrictLookup,
  type Flags,
  type Page,
  type PageLine,
  type Step,
} from './flow';
import type { AskRequest } from './ask';
import { Arrow, ChoiceInput, ComposerBox, PhoneSubmitForm, TextStepInput, ZipInput } from './inputs';
import { leadEventId, loadLeadTracking, submitChatLead } from './lead-submit';
import { artForStep, sellArt } from './art';
import { ChatFooter } from './chat-footer';
import { displayPhone } from './chat-header';

// One line-art illustration per question, drawn for the test prep chat, by the legacy answer key; any
// other question (the SAT section, IEP/504, exam timing…) gets the grade scene.
/** The chat's saved answers (session storage), so a refresh resumes where the visitor was. */
const STATE_KEY = 'vt-test-prep-chat-v1';
/** Reassurance pages the visitor has said yes to (kept for a refresh, like the answers). */
const YES_KEY = 'vt-test-prep-yes-v1';
/** Questions typed into the message box this visit. */
const ASKS_KEY = 'vt-test-prep-asks-v1';
const ASKS_MAX = 20;
// Keep in step with ASK_HISTORY_MAX in ask.ts (the server rejects longer histories).
const ASK_HISTORY_MAX = 6;
/** Luna's endpoint (the app's basePath is /df). */
const ASK_ENDPOINT = '/df/api/test-prep/ask';

/**
 * A statement page waits for the visitor to say yes (no auto-advance), so they get in the habit of
 * agreeing before the contact step. The wording rotates so it doesn't feel like a stock button.
 */
const YES_LABELS: ReadonlyArray<string> = ['Yes, sounds good', 'Yes, keep going', 'Yes, I like that', 'Yes, let’s do it'];

const PARTNER_BENEFITS = (discount: number): string[] => [`${discount}% off all tutoring programs`, 'Accelerated tutor placement times', 'Dedicated customer support', 'And more!'];

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The page index stored on the current browser history entry (null when the entry isn't the chat's yet). */
const historyPage = (): number | null => {
  const page = (window.history.state as { tpcPage?: unknown } | null)?.tpcPage;
  return typeof page === 'number' ? page : null;
};

// Spread the current entry so Next's router keeps its own history state (app-router.js copies __NA etc.).
const writeHistory = (page: number, push: boolean) => {
  const data = { ...(window.history.state as object | null), tpcPage: page };
  if (push) window.history.pushState(data, '');
  else window.history.replaceState(data, '');
};

const readStart = (form: ChatForm): { state: ChatState; restored: boolean } => {
  try {
    const saved = parseState(window.sessionStorage.getItem(STATE_KEY), form);
    if (saved) return { state: saved, restored: true };
  } catch {
    // Storage may be unavailable in a restricted browser; the chat starts fresh.
  }
  return { state: newState(form), restored: false };
};

// Trustpilot's stars as on the test prep home page's ratings (4.4: four and a half stars).
const TP_FILLS = ['100%', '100%', '100%', '100%', '50%'] as const;

/** Trustpilot's five green stars, filling in one at a time once their page is in view. */
const TrustpilotStars = () => (
  <span className="tp-stars tpc-tp-stars" aria-hidden="true">
    {TP_FILLS.map((fill, i) => (
      <i key={i} style={{ '--i': i, '--fill': fill } as CSSProperties}>
        <svg viewBox="0 0 24 24">
          <path d={TP_STAR_PATH} />
        </svg>
      </i>
    ))}
  </span>
);

/**
 * The site's two approved ratings side by side, as chips, right before the visitor shares their details: the
 * 4.9 session rating with gold stars, and Trustpilot's 4.4 with its green ones.
 */
function RatingsCard() {
  return (
    <ul className="tpc-ratings" aria-label="Ratings">
      <li className="tpc-rating">
        <span className="tpc-rating-top">
          <b className="tpc-rating-num">
            4.9<span className="sr-only"> out of 5</span>
          </b>
          <RatingStars />
        </span>
        <span className="tpc-rating-cap">across 3.4M session ratings</span>
      </li>
      <li className="tpc-rating">
        <span className="tpc-rating-top">
          <b className="tpc-rating-num">
            4.4<span className="sr-only"> out of 5</span>
          </b>
          <TrustpilotStars />
        </span>
        <span className="tpc-rating-cap">
          Excellent on Trustpilot, <span className="nw">13,500+ reviews</span>
        </span>
      </li>
    </ul>
  );
}

const ROADMAP_ICONS: Record<(typeof ROADMAP.steps)[number]['icon'], ReactNode> = {
  chat: <path d="M5 6.5h14a1.5 1.5 0 0 1 1.5 1.5v7a1.5 1.5 0 0 1-1.5 1.5H11l-4 3v-3H5A1.5 1.5 0 0 1 3.5 15V8A1.5 1.5 0 0 1 5 6.5zM8 11.5h.01M12 11.5h.01M16 11.5h.01" />,
  match: (
    <>
      <circle cx="8.5" cy="8.5" r="3" />
      <circle cx="15.5" cy="8.5" r="3" />
      <path d="M3.5 19c.6-3 2.6-4.7 5-4.7s4.4 1.7 5 4.7M10.5 19c.6-3 2.6-4.7 5-4.7s4.4 1.7 5 4.7" />
    </>
  ),
  star: <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.8l-5.2 2.8 1-5.8-4.3-4.1 5.9-.9z" />,
};

/**
 * Step 3's picture: a live video-call tile of a tutor. The shot drifts slowly like a camera, a "Live" dot
 * pulses and the mic bars move, so it reads as a session in progress.
 */
function LiveTutorTile() {
  return (
    <span className="tpc-road-video" aria-hidden="true">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={asset('img/nerdy-tutor-live.webp')} alt="" width={320} height={320} decoding="async" />
      <span className="tpc-road-live">
        <i />
        Live
      </span>
      <span className="tpc-road-bars">
        <i />
        <i />
        <i />
      </span>
      <span className="tpc-road-cam">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="7" width="12" height="10" rx="2" />
          <path d="M15 11l6-3v8l-6-3z" />
        </svg>
      </span>
    </span>
  );
}

/** "Here's how this works": the three steps as a little path, popping in one at a time, with "You're here" on the first. */
function RoadmapCard() {
  return (
    <ol className="tpc-roadmap">
      {ROADMAP.steps.map((step, i) => (
        <li key={step.title} className={i === 0 ? 'is-now' : undefined} style={{ '--i': i } as CSSProperties}>
          {step.icon === 'star' ? (
            <LiveTutorTile />
          ) : (
            <span className="tpc-road-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                {ROADMAP_ICONS[step.icon]}
              </svg>
            </span>
          )}
          <span className="tpc-road-text">
            <span className="tpc-road-title">
              <span className="sr-only">Step {i + 1}: </span>
              {step.title}
            </span>
            <span className="tpc-road-note">{step.note}</span>
          </span>
          {i === 0 ? <span className="tpc-road-here">You&apos;re here</span> : null}
        </li>
      ))}
    </ol>
  );
}

/** The form's partner-school panel as a card: what families in a partner district get. */
function PartnerCard() {
  return (
    <div className="tpc-card tpc-partner">
      <p>Parents with children in your district have access to a host of benefits including:</p>
      <ul>
        {PARTNER_BENEFITS(10).map((benefit, i) => (
          <li key={benefit} style={{ '--i': i } as CSSProperties}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12.5l4.2 4.2L19 7" />
            </svg>
            {benefit}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Confetti for the partner school page: a fan of pieces that burst upward out of the benefits card and fall.
// Spread, spin and delay come from the index, so every render draws the same burst.
const CONFETTI_COLORS = ['#ffcd32', '#ff616e', '#8fcbe0', '#bfd3b9', '#3a53a2', '#ffe38a'] as const;
const CONFETTI = Array.from({ length: 28 }, (_, i) => {
  const angle = ((-90 + (((i * 73) % 150) - 75)) * Math.PI) / 180;
  const distance = 80 + ((i * 53) % 120);
  return {
    x: Math.round(Math.cos(angle) * distance),
    y: Math.round(Math.sin(angle) * distance),
    spin: ((i * 97) % 720) - 360,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    delay: (i * 17) % 160,
  };
});

function Confetti() {
  return (
    <span className="tpc-confetti" aria-hidden="true">
      {CONFETTI.map((piece, i) => (
        <i key={i} style={{ '--x': `${piece.x}px`, '--y': `${piece.y}px`, '--r': `${piece.spin}deg`, '--c': piece.color, '--d': `${piece.delay}ms` } as CSSProperties} />
      ))}
    </span>
  );
}

/** How long "Great news!" holds before the district's line writes itself. */
/** The standard form's phone panel, for its analytics. */
const PHONE_PANEL_ID = 'phone-reg';
const PARTNER_NEWS_HOLD_MS = 600;
/**
 * How long the card and its confetti get before Continue appears: the burst starts 120ms in and its pieces
 * run 1.7s each, staggered up to 160ms, so Continue comes once the last piece has landed.
 */
const PARTNER_CARD_HOLD_MS = 2000;

/**
 * The partner school page as a reveal: "Great news!" writes itself, holds, then the district's line
 * types out, then the benefits card rises in with confetti bursting out of it. Continue is there from the
 * start, as on the standard form, so nobody waits for the show to finish.
 */
function PartnerReveal({
  page,
  index,
  active,
  art,
  onContinue,
}: {
  page: Extract<Page, { kind: 'question' }>;
  index: number;
  active: boolean;
  art: string | null;
  onContinue: () => void;
}) {
  // news: "Great news!" types; title: the district's line types; card: the card rises and the confetti
  // bursts; done: the show is over.
  const [phase, setPhase] = useState<'news' | 'title' | 'card' | 'done'>(() => (typeof window !== 'undefined' && prefersReducedMotion() ? 'done' : 'news'));
  const [burst, setBurst] = useState(false);
  const hold = useRef(0);
  useEffect(() => () => window.clearTimeout(hold.current), []);
  useEffect(() => {
    if (phase !== 'card') return;
    // The burst starts just after the card begins to rise, so it reads as coming out of the card.
    const burstTimer = window.setTimeout(() => setBurst(true), 120);
    const doneTimer = window.setTimeout(() => setPhase('done'), PARTNER_CARD_HOLD_MS);
    return () => {
      window.clearTimeout(burstTimer);
      window.clearTimeout(doneTimer);
    };
  }, [phase]);

  const handleNewsDone = () => {
    window.clearTimeout(hold.current);
    hold.current = window.setTimeout(() => setPhase((current) => (current === 'news' ? 'title' : current)), PARTNER_NEWS_HOLD_MS);
  };
  const cardShown = phase === 'card' || phase === 'done';
  // Continue comes last, once the heading, the card and the confetti have all played.
  const shown = phase === 'done';
  const headingId = `tpc-h-${page.key}`;

  return (
    <PageFrame
      page={page}
      index={index}
      active={active}
      art={art}
      head={
        <>
          <TypedHeading as="p" className="tpc-partner-news tpc-hand" text="Great news!" active={active} onDone={handleNewsDone} />
          {phase === 'news' ? (
            <h2 className="sr-only" id={headingId}>
              {page.question}
            </h2>
          ) : (
            <TypedHeading id={headingId} text={page.question} active={active} onDone={() => setPhase((current) => (current === 'title' ? 'card' : current))} />
          )}
        </>
      }
    >
      <div className={['tpc-partner-wrap', cardShown ? 'is-shown' : '', burst ? 'is-burst' : ''].filter(Boolean).join(' ')}>
        <Confetti />
        <PartnerCard />
      </div>
      <div className={shown ? 'tpc-actions tpc-partner-actions is-shown' : 'tpc-actions tpc-partner-actions'} aria-hidden={!shown}>
        <button className="tpc-go tpc-yes" type="button" tabIndex={shown ? 0 : -1} onClick={onContinue}>
          Continue <Arrow />
        </button>
        <span className="tpc-enter" aria-hidden="true">
          press <b>Enter ↵</b>
        </span>
      </div>
    </PageFrame>
  );
}

/** A line of copy or a card on a page. */
function Line({ line }: { line: PageLine }) {
  if ('card' in line) {
    if (line.card === 'roadmap') return <RoadmapCard />;
    if (line.card === 'ratings') return <RatingsCard />;
    if (line.card === 'partner') return <PartnerCard />;
    return null;
  }
  return <p className="tpc-line">{line.text}</p>;
}

/**
 * One screen of the form, as wide as the chat box: the heading across the top, then the answers on the
 * left half and the art on the right half (on phones the art sits small above the heading instead).
 */
function PageFrame({
  page,
  index,
  active,
  art,
  head,
  children,
  kicker,
  typedMs,
}: {
  page: { kind: string; key: string };
  index: number;
  active: boolean;
  art: string | null;
  head: ReactNode;
  children: ReactNode;
  /** The page's headline, above the art (the first question's page). */
  kicker?: ReactNode;
  /** How long the question takes to type in (ms): the answers come in after it. */
  typedMs?: number;
}) {
  const image = (place: 'top' | 'side') =>
    art ? (
      <div className={`tpc-page-art is-${place}`} aria-hidden="true">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={asset(`img/${art}`)} alt="" width={760} height={572} decoding="async" loading={index < 2 ? 'eager' : 'lazy'} />
      </div>
    ) : null;
  return (
    <section
      className={['tpc-page', `is-${page.kind}`, active ? 'is-active' : '', art ? 'has-art' : ''].filter(Boolean).join(' ')}
      data-page={index}
      aria-labelledby={`tpc-h-${page.key}`}
      style={typedMs ? ({ '--tpc-typed': `${typedMs}ms` } as CSSProperties) : undefined}
    >
      <div className="tpc-page-in">
        {kicker}
        {image('top')}
        {/* The question, its text and the answers sit on a solid panel, so art above can never show through. */}
        <div className="tpc-page-main">
          <div className="tpc-page-head">{head}</div>
          <div className="tpc-page-row">
            <div className="tpc-page-body">{children}</div>
            {image('side')}
          </div>
        </div>
      </div>
    </section>
  );
}

/** A question typed into the message box, answered by Luna on its own page after the page it was asked from. */
type Ask = { id: string; after: string; question: string; answer: string; status: 'waiting' | 'streaming' | 'done' | 'error' };

/** A page of the form, or an asked question's page. */
type ViewPage = Page | { kind: 'ask'; key: string; ask: Ask };

const readYes = (): Set<string> => {
  try {
    const raw: unknown = JSON.parse(window.sessionStorage.getItem(YES_KEY) ?? '[]');
    return new Set(Array.isArray(raw) ? raw.filter((key): key is string => typeof key === 'string' && key.startsWith('s-')) : []);
  } catch {
    return new Set();
  }
};

const readAsks = (): Ask[] => {
  try {
    const raw: unknown = JSON.parse(window.sessionStorage.getItem(ASKS_KEY) ?? '[]');
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((item): Ask[] => {
      if (!item || typeof item !== 'object') return [];
      const { id, after, question, answer } = item as Record<string, unknown>;
      if (typeof id !== 'string' || typeof after !== 'string' || typeof question !== 'string' || typeof answer !== 'string' || !answer) return [];
      return [{ id, after, question: question.slice(0, 500), answer: answer.slice(0, 1500), status: 'done' }];
    });
  } catch {
    return [];
  }
};

/**
 * The pages the visitor can reach: every page, each followed by the questions asked from it, but stopping at
 * the first reassurance page they haven't said yes to (its asked questions included). Scrolling can't skip a
 * Yes: the next question only exists once they've given it.
 */
const reachable = (pages: ReadonlyArray<Page>, asks: ReadonlyArray<Ask>, yes: ReadonlySet<string>): ViewPage[] => {
  const view: ViewPage[] = [];
  for (const page of pages) {
    view.push(page);
    asks.filter((ask) => ask.after === page.key).forEach((ask) => view.push({ kind: 'ask', key: `a-${ask.id}`, ask }));
    if (page.kind === 'statement' && !yes.has(page.key)) break;
  }
  return view;
};

/**
 * A reassurance headline that types itself in the first time its page comes into view, the way the test prep
 * home page's chat box types its examples (LLM-like chunks). The full text is there for screen readers, and
 * an invisible copy holds the space so nothing moves while it types.
 */
function TypedHeading({
  id,
  text,
  active,
  as = 'h2',
  className = 'tpc-statement-h',
  onDone,
}: {
  id?: string;
  text: string;
  active: boolean;
  as?: 'h2' | 'p';
  className?: string;
  /** Called once the text has finished typing (at once under reduced motion). */
  onDone?: () => void;
}) {
  // The first page also renders on the server, where there is no motion setting to read.
  const [typed, setTyped] = useState<number | null>(() => (typeof window !== 'undefined' && prefersReducedMotion() ? text.length : null));
  // Starts typing the first time the page is in view (and stays started when it scrolls away).
  const [started, setStarted] = useState(false);
  if (active && !started) setStarted(true);
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  });

  // The whole run lives in one effect with its own cleanup, so a re-mount (or new text) types it over
  // from the start instead of stopping halfway.
  useEffect(() => {
    if (!started) return;
    if (prefersReducedMotion()) {
      onDoneRef.current?.();
      return;
    }
    const ends = typingSteps(text);
    let k = 0;
    let timer = 0;
    const step = () => {
      setTyped(ends[k] ?? text.length);
      k += 1;
      if (k < ends.length) timer = window.setTimeout(step, streamDelay());
      else onDoneRef.current?.();
    };
    timer = window.setTimeout(step, 260);
    return () => window.clearTimeout(timer);
  }, [started, text]);

  const done = typed !== null && typed >= text.length;
  const Tag = as;
  return (
    <Tag className={done ? `${className} tpc-tw` : `${className} tpc-tw is-typing`} id={id} tabIndex={as === 'h2' ? -1 : undefined}>
      <span className="sr-only">{text}</span>
      <span className="tpc-tw-ghost" aria-hidden="true">
        {text}
      </span>
      <span className="tpc-tw-live" aria-hidden="true">
        {text.slice(0, typed ?? 0)}
        {typed !== null && !done ? <i className="hc-caret" /> : null}
      </span>
    </Tag>
  );
}

/** Inside the answer box while Luna starts: just the words (the logo mark beside the box does the animating). */
function Thinking() {
  return (
    <p className="tpc-think" role="status">
      Thinking
      <span className="tpc-think-dots" aria-hidden="true">
        <span>.</span>
        <span className="is-second">.</span>
        <span className="is-third">.</span>
      </span>
    </p>
  );
}

/** Luna's answer, with **bold** kept (the only Markdown the prompt allows). */
function AskText({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return <>{parts.map((part, i) => (i % 2 ? <b key={i}>{part}</b> : part))}</>;
}

/** The logo mark on the right of Luna's answer, level with its top: its three pieces pulse in turn while Luna writes. */
function AnswerMark({ busy }: { busy: boolean }) {
  return (
    <span className={busy ? 'tpc-mark is-busy' : 'tpc-mark'} aria-hidden="true">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img className="tpc-mark-piece is-left" src={markPiece('leftonly')} alt="" width={137} height={128} />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img className="tpc-mark-piece is-right" src={markPiece('rightonly')} alt="" width={137} height={128} />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img className="tpc-mark-piece is-dot" src={markPiece('reddotonlyonly')} alt="" width={137} height={128} />
    </span>
  );
}

/** The page after `key` that belongs to the form (skipping asked-question pages), or -1. */
const nextFormPage = (list: ReadonlyArray<ViewPage>, key: string): number => {
  const at = list.findIndex((page) => page.key === key);
  if (at < 0) return -1;
  for (let i = at + 1; i < list.length; i++) if (list[i].kind !== 'ask') return i;
  return -1;
};

/** A question page as the PostHog lead form funnel names it (the legacy panel id and answer key). */
const leadFormStepFor = (page: Extract<Page, { kind: 'question' }>): LeadFormStep => ({
  panelId: page.step.panelId,
  key: page.step.key,
  number: page.number,
  type: page.step.kind,
  question: page.question,
});

/** One id per page view, so the questions asked in one visit group together in PostHog. */
const newChatId = () => `lfc_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

/**
 * Deep links: each page of the form has a #hash, kept in the address bar as the visitor moves, so a reload
 * (or a shared link) comes back to it. Questions use their step (#how-soon, #grade-level), reassurance pages
 * add "-note" (#who-needs-help-note), and an asked question's page keeps the hash of the page it was asked from.
 */
const hashFor = (page: ViewPage | undefined): string | null => {
  const key = page?.kind === 'ask' ? page.ask.after : page?.key;
  if (!key) return null;
  if (key.startsWith('q-')) return key.slice(2);
  if (key.startsWith('s-')) return `${key.slice(2)}-note`;
  return null;
};

/** The page key a #hash names ("#who-needs-help-note" is "s-who-needs-help"), or null. */
const keyForHash = (hash: string): string | null => {
  const slug = decodeURIComponent(hash.replace(/^#/, '')).trim().toLowerCase();
  if (!/^[a-z0-9][a-z0-9-]*$/.test(slug)) return null;
  return slug.endsWith('-note') ? `s-${slug.slice(0, -5)}` : `q-${slug}`;
};

/**
 * Where a deep link lands: the page it names, if the visitor has got that far (answered it, or it's the
 * one waiting for them), else the furthest step they've reached (which may be the first).
 */
const deepLinkTarget = (full: ReadonlyArray<ViewPage>, hash: string): string | undefined => {
  const key = keyForHash(hash);
  if (key && full.some((page) => page.key === key)) return key;
  const form = full.filter((page) => page.kind !== 'ask');
  return form[form.length - 1]?.key;
};

/**
 * The legacy lead form (any subject page) as a paged chat, like a typeform: one question per screen, snapping from page to
 * page as the visitor scrolls or swipes, or taps Back. Answered pages stay, so the visitor can page back,
 * change an answer and carry on. The message box stays pinned at the bottom: a typed answer that names an
 * option answers the question, and anything else gets a short note pointing to an education consultant.
 */
export function ChatFlow({ form, phone, headline, copyrightYear }: { form: ChatForm; phone: string; headline?: string; copyrightYear: number }) {
  // A new chat from the first render, so the server sends the first question in the page and it shows before
  // the code loads (an empty screen lost visitors). On mount, a saved chat replaces it (the start effect).
  const [state, setState] = useState<ChatState | null>(() => newState(form));
  // True once the start effect has loaded the saved chat (or started a new one) in the browser.
  const [ready, setReady] = useState(false);
  const [flags, setFlags] = useState<Flags>({});
  const [lookup, setLookup] = useState<DistrictLookup | undefined>(undefined);
  const [asks, setAsks] = useState<Ask[]>([]);
  const [yes, setYes] = useState<ReadonlySet<string>>(() => new Set());
  const [active, setActive] = useState(0);
  // Bumped to carry out a pending move when nothing else re-renders (a repeat Yes, a typed #hash).
  const [moveTick, setMoveTick] = useState(0);
  const started = useRef(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef(0);
  // The page a programmatic move is heading to, until that scroll has settled.
  const moving = useRef<{ index: number; until: number } | null>(null);
  // Where to move once the pages have re-rendered (after an answer, a question, a refresh or a Yes).
  const pending = useRef<{ find: (list: ReadonlyArray<ViewPage>) => number; smooth: boolean; push: boolean } | null>(null);
  const eventId = useRef<string | null>(null);
  const failures = useRef(0);
  const pixelInitialized = useRef(false);
  const asking = useRef<AbortController | null>(null);
  const chatId = useRef<string>('');

  const pages = useMemo(() => (state ? buildPages(state, lookup, flags, form) : []), [state, lookup, flags, form]);
  const ctx = useMemo<Ctx | null>(() => (state ? ctxFor(state, lookup, flags, form) : null), [state, lookup, flags, form]);
  // Each asked question sits right after the page it was asked from (dropped if that page is gone), and
  // nothing past the page the visitor is on is shown.
  const full = useMemo<ViewPage[]>(() => reachable(pages, asks, yes), [asks, pages, yes]);
  // The visitor can scroll back and forward over everything answered, up to the first question (or Yes)
  // still waiting for them.
  const view = full;
  const last = view.length - 1;
  const viewRef = useRef<ViewPage[]>([]);
  useEffect(() => {
    viewRef.current = view;
  });

  // Start: the saved chat (back where they left off), else a new one.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const { state: start, restored } = readStart(form);
    const startFlags = flagsFrom(window.location.search);
    const startPages = buildPages(start, undefined, startFlags, form);
    const startAsks = restored ? readAsks() : [];
    const startYes = restored ? readYes() : new Set<string>();
    // Where to start: the page the #hash names, if they've got that far; else, back where they left off (the
    // last page of the form they can reach: a question, or a Yes still to give); a new visit starts at the top.
    const startFull = reachable(startPages, startAsks, startYes);
    const frontier = restored || window.location.hash ? deepLinkTarget(startFull, window.location.hash) : startPages[0]?.key;
    pending.current = { find: (list) => list.findIndex((page) => page.key === frontier), smooth: false, push: false };
    if (historyPage() === null) writeHistory(0, false);
    // The PostHog lead form funnel (the same events as the standard form).
    trackLeadFormShown({ variant: 'chat', subject: form.subject }, { panelCount: form.steps.length });
    setFlags(startFlags);
    setAsks(startAsks);
    setYes(startYes);
    setState(start);
    setReady(true);
  }, [form]);

  // The school districts for the answered ZIP. The lookup page holds the question's place; once the
  // districts are known the pages settle.
  const zip = state?.answers.zipcode;
  const zipPageKey = `q-${form.steps.find((step) => step.kind === 'zip')?.id ?? 'zipcode'}`;
  const needsLookup = Boolean(zip) && pages.some((page) => page.kind === 'loading');
  // The ZIP whose lookup is running (or done), so a re-render never starts it twice or discards its result.
  const lookupZip = useRef<string | null>(null);
  useEffect(() => {
    if (!zip || !needsLookup || lookupZip.current === zip) return;
    lookupZip.current = zip;
    setLookup({ zip, districts: null });
    void fetchDistrictsForZip(zip).then((districts) => {
      // A newer ZIP has its own lookup; this one is stale.
      if (lookupZip.current !== zip) return;
      // If the visitor is waiting on the lookup page, carry on to whatever comes after the ZIP question.
      if (viewRef.current[activeRef.current]?.kind === 'loading') {
        pending.current = { find: (list) => nextFormPage(list, zipPageKey), smooth: true, push: false };
      }
      setLookup({ zip, districts });
    });
  }, [zip, needsLookup, zipPageKey]);

  // The chat fills exactly the part of the screen the visitor can see: sized from the visual viewport, so it
  // sits above Safari's floating toolbar and above the phone keyboard when that opens, and re-measured as
  // either moves. The page behind it never scrolls (iOS likes to scroll it when the keyboard opens), and the
  // header's height is tracked too.
  useEffect(() => {
    const root = document.documentElement;
    const header = document.getElementById('top');
    const viewport = window.visualViewport;
    const previousRootOverflow = root.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    root.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    const update = () => {
      if (window.scrollY !== 0) window.scrollTo(0, 0);
      if (header) root.style.setProperty('--tpc-hdr', `${header.offsetHeight}px`);
      if (viewport) {
        root.style.setProperty('--tpc-vvh', `${Math.round(viewport.height)}px`);
        root.style.setProperty('--tpc-vvtop', `${Math.round(viewport.offsetTop)}px`);
        root.classList.toggle('tpc-keyboard', window.innerHeight - viewport.height > 120);
      }
    };
    update();
    const observer = typeof ResizeObserver === 'undefined' || !header ? null : new ResizeObserver(update);
    if (header) observer?.observe(header);
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      root.style.overflow = previousRootOverflow;
      document.body.style.overflow = previousBodyOverflow;
      ['--tpc-hdr', '--tpc-vvh', '--tpc-vvtop'].forEach((name) => root.style.removeProperty(name));
      root.classList.remove('tpc-keyboard');
    };
  }, []);

  // Same Meta Pixel start-up as InteractiveForm; the layout does not load it.
  useEffect(() => {
    if (pixelInitialized.current || !ENV.META_PIXEL_ID) return;
    pixelInitialized.current = true;
    let timer = 0;
    void loadLeadTracking()
      .then(({ loadMetaPixelScript, setupPixelStub }) => {
        setupPixelStub();
        window.fbq('init', ENV.META_PIXEL_ID);
        window.fbq('track', 'PageView');
        timer = window.setTimeout(() => loadMetaPixelScript(ENV.META_PIXEL_ID), 2000);
      })
      .catch(() => {});
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Not before the start effect has read the saved chat (the first render's new chat would overwrite it).
    if (!state || !ready) return;
    try {
      // Contact details are never saved: a refresh asks for them again.
      const answers = Object.fromEntries(Object.entries(state.answers).filter(([key]) => !CONTACT_KEYS.includes(key)));
      window.sessionStorage.setItem(STATE_KEY, JSON.stringify({ ...state, answers }));
    } catch {
      // Browser storage is optional.
    }
  }, [state, ready]);

  // Answered questions are kept for a refresh, like the answers (contact details never are).
  useEffect(() => {
    if (!started.current) return;
    try {
      const done = asks.filter((ask) => ask.status === 'done').map(({ id, after, question, answer }) => ({ id, after, question, answer }));
      window.sessionStorage.setItem(ASKS_KEY, JSON.stringify(done));
    } catch {
      // Browser storage is optional.
    }
  }, [asks]);

  useEffect(() => () => asking.current?.abort(), []);

  useEffect(() => {
    if (!started.current) return;
    try {
      window.sessionStorage.setItem(YES_KEY, JSON.stringify([...yes]));
    } catch {
      // Browser storage is optional.
    }
  }, [yes]);

  const pageEl = useCallback((index: number) => stageRef.current?.querySelector<HTMLElement>(`[data-page="${index}"]`) ?? null, []);

  /** Puts page `index` exactly at the top of the stage, right away. */
  const alignTo = useCallback(
    (index: number) => {
      const stage = stageRef.current;
      const el = pageEl(index);
      if (stage && el && Math.abs(stage.scrollTop - el.offsetTop) > 1) stage.scrollTo({ top: el.offsetTop, behavior: 'auto' });
    },
    [pageEl]
  );

  const goTo = useCallback(
    (index: number, smooth = true) => {
      const stage = stageRef.current;
      const el = pageEl(index);
      if (!stage || !el) return;
      const animate = smooth && !prefersReducedMotion() && Math.abs(stage.scrollTop - el.offsetTop) > 1;
      // While this move runs, the scroll handler leaves the page in view alone (no in-between pages).
      moving.current = animate ? { index, until: performance.now() + 1200 } : null;
      stage.scrollTo({ top: el.offsetTop, behavior: animate ? 'smooth' : 'auto' });
      activeRef.current = index;
      setActive(index);
    },
    [pageEl]
  );

  // Move to the pending page once it exists, and put focus on its heading for screen readers and keys.
  // Moving forward to a page reached for the first time adds a browser history entry for it, so the
  // browser's back button pages back through the form (and off it from the first page).
  useEffect(() => {
    const target = pending.current;
    // The first render shows a new chat (sent with the page); keep the start target (a deep link, or back
    // where they left off) until the saved chat has become pages, or every reload would land on the first page.
    if (!target || !ready || !view.length) return;
    pending.current = null;
    const index = target.find(view);
    if (index < 0) return;
    if (target.push && index > (historyPage() ?? 0)) writeHistory(index, true);
    goTo(index, target.smooth);
    pageEl(index)?.querySelector<HTMLElement>('.tpc-q, .tpc-statement-h, .tpc-ask-q')?.focus({ preventScroll: true });
  }, [goTo, pageEl, view, moveTick, ready]);

  // When the stage changes size (the phone keyboard closing after a send, rotation, the browser bar), every
  // page changes height with it: put the page in view straight back at the top, with no in-between frame.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return;
    let height = stage.clientHeight;
    // Every page is at least the stage's height (so the last page can always reach the top).
    stage.style.setProperty('--tpc-stage-h', `${height}px`);
    const observer = new ResizeObserver(() => {
      if (stage.clientHeight === height) return;
      height = stage.clientHeight;
      stage.style.setProperty('--tpc-stage-h', `${height}px`);
      alignTo(moving.current?.index ?? activeRef.current);
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, [alignTo]);

  // The page in view: the last one whose top is above the middle of the stage.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    let frame = 0;
    let settle = 0;
    // A programmatic move is over when scrolling stops (scrollend, or a quiet 150ms where it isn't
    // supported): if it came to rest off the page (the page grew or the stage resized on the way), land it.
    const finishMove = () => {
      const target = moving.current;
      if (!target) return;
      moving.current = null;
      // The form's own move is done: a gesture from before it doesn't excuse a later re-snap.
      lastInput = 0;
      alignTo(target.index);
    };
    // The page in view only changes when the visitor scrolls (wheel, touch, keys, the scrollbar) or the form
    // moves itself. Any other scroll is the browser re-snapping to the page it last remembered as snapped (it
    // does this when a page's content changes size, such as an answer starting to stream in) and it would
    // leave part of the previous page showing: put the page in view straight back.
    // Only real scroll gestures count: a tap on a button (Back to question 3) is not one.
    let lastInput = 0;
    const markInput = () => {
      lastInput = performance.now();
    };
    const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);
    const markKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (SCROLL_KEYS.has(event.key) && !target?.closest('input, textarea, select, [contenteditable="true"]')) markInput();
    };
    const USER_SCROLL_MS = 900;
    const handleScroll = () => {
      const target = moving.current;
      if (target) {
        window.clearTimeout(settle);
        if (performance.now() > target.until) finishMove();
        else settle = window.setTimeout(finishMove, 150);
        return;
      }
      if (performance.now() - lastInput > USER_SCROLL_MS) {
        alignTo(activeRef.current);
        return;
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const line = stage.scrollTop + stage.clientHeight * 0.45;
        let index = 0;
        stage.querySelectorAll<HTMLElement>('[data-page]').forEach((el, i) => {
          if (el.offsetTop <= line) index = i;
        });
        if (index !== activeRef.current) {
          activeRef.current = index;
          setActive(index);
        }
      });
    };
    const INPUTS = ['wheel', 'touchmove'] as const;
    INPUTS.forEach((type) => stage.addEventListener(type, markInput, { passive: true }));
    // Keys scroll the stage from anywhere on the page (arrows, Page Down, space).
    window.addEventListener('keydown', markKey, { passive: true });
    stage.addEventListener('scroll', handleScroll, { passive: true });
    stage.addEventListener('scrollend', finishMove);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      INPUTS.forEach((type) => stage.removeEventListener(type, markInput));
      window.removeEventListener('keydown', markKey);
      stage.removeEventListener('scroll', handleScroll);
      stage.removeEventListener('scrollend', finishMove);
    };
  }, [alignTo, state]);

  // Keep the page in view in the address bar (#section, #learner-note…), without adding history entries.
  const activePageHash = hashFor(view[Math.min(active, last)]);
  useEffect(() => {
    // Not before the start effect has read the #hash (a deep link, or the page a reload was on).
    if (!ready || !activePageHash || window.location.hash === `#${activePageHash}`) return;
    const url = `${window.location.pathname}${window.location.search}#${activePageHash}`;
    window.history.replaceState(window.history.state, '', url);
  }, [activePageHash, ready]);

  // A hash typed or pasted into the address bar moves there too (or to the furthest step they've reached).
  const fullRef = useRef<ViewPage[]>([]);
  useEffect(() => {
    fullRef.current = full;
  });
  useEffect(() => {
    const handleHash = () => {
      const key = deepLinkTarget(fullRef.current, window.location.hash);
      if (!key) return;
      pending.current = { find: (list) => list.findIndex((page) => page.key === key), smooth: true, push: false };
      setMoveTick((tick) => tick + 1);
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  useEffect(() => {
    const handlePop = (event: PopStateEvent) => {
      const target = (event.state as { tpcPage?: unknown } | null)?.tpcPage;
      if (typeof target !== 'number') return;
      // One page back from where the visitor is, even if they scrolled since this entry was made.
      const index = Math.min(target, activeRef.current - 1);
      if (index < 0) {
        window.history.back();
        return;
      }
      goTo(Math.min(index, last));
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [goTo, last]);

  /** On to the next page of the form after `key` (past any asked-question pages). */
  const moveOnFrom = (key: string) => {
    pending.current = { find: (list) => nextFormPage(list, key), smooth: true, push: true };
    setMoveTick((tick) => tick + 1);
  };

  const handleAnswer = (key: string, step: Step, value: Answer, inputMethod: InputMethod = 'easy_choice', typed?: string) => {
    moveOnFrom(key);
    setState((previous) => (previous ? withAnswer(previous, step, value) : previous));
    // The same step analytics as the standard form.
    void import('@/app/utils/web-analytics').then(({ trackFormStepComplete }) => trackFormStepComplete(step.panelId)).catch(() => {});
    const page = pages.find((item) => item.key === key);
    if (page?.kind === 'question') trackLeadFormStepCompleted(leadFormStepFor(page), { answer: value, inputMethod, typedText: typed });
    // As the standard form: Lead Form Started when the first question is answered.
    if (step.id === form.steps[0]?.id) {
      void loadLeadTracking()
        .then(({ trackMetaEvent }) => trackMetaEvent('LeadFormStarted', { content_category: form.subject || 'general' }, { sessionDedupKey: 'meta_lead_form_started' }))
        .catch(() => {});
    }
  };

  const handleYes = (key: string) => {
    // The Yes opens the next page (recording it, the first time): move once that page has rendered.
    moveOnFrom(key);
    if (key.startsWith('s-')) setYes((previous) => (previous.has(key) ? previous : new Set(previous).add(key)));
  };

  /**
   * After an asked question: back to the question it was asked from, if that needs a real answer. Asked from a
   * page whose only answer is "carry on" (a reassurance with its Yes, the partner page with its Continue),
   * "Keep going" counts as that answer and moves straight on to the next page.
   */
  const handleAskDone = (ask: Ask) => {
    const from = view.findIndex((page) => page.key === ask.after);
    const origin = view[from];
    if (origin?.kind === 'question' && origin.step.statement && ctx) {
      handleAnswer(origin.key, origin.step, optionsFor(origin.step, ctx)[0]?.value ?? 'continue');
    } else if (origin?.kind === 'question') {
      goTo(from);
      pageEl(from)?.querySelector<HTMLElement>('.tpc-q, .tpc-statement-h')?.focus({ preventScroll: true });
    } else if (origin?.kind === 'statement') {
      handleYes(origin.key);
    } else handleYes(`a-${ask.id}`);
  };

  /** A typed question: a new page right after the one in view, with Luna's answer streaming in. */
  const handleAsk = async (text: string) => {
    if (!state || !ctx) return;
    const from = view[Math.min(activeRef.current, last)];
    if (!from || from.kind === 'loading') return;
    // Asked from an answer page, the question belongs to the page that answer follows.
    const after = from.kind === 'ask' ? from.ask.after : from.key;
    // Close the phone keyboard before moving, so the page doesn't resize mid-scroll.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const ask: Ask = { id, after, question: text.trim().slice(0, 500), answer: '', status: 'waiting' };
    const update = (patch: Partial<Ask>) => setAsks((list) => list.map((item) => (item.id === id ? { ...item, ...patch } : item)));
    pending.current = { find: (list) => list.findIndex((page) => page.key === `a-${id}`), smooth: true, push: true };
    setAsks((list) => [...list, ask].slice(-ASKS_MAX));

    const origin = pages.find((page) => page.key === after);
    chatId.current ||= newChatId();
    const ids = await leadFormPostHogIds();
    const request: AskRequest = {
      question: ask.question,
      history: asks
        .filter((item) => item.status === 'done')
        .slice(-ASK_HISTORY_MAX)
        .map((item) => ({ question: item.question, answer: item.answer.slice(0, 1500) })),
      survey: {
        subject: form.subjectLabel,
        phone: displayPhone(phone),
        answers: pages.flatMap((page) =>
          page.kind === 'question' && page.answer !== undefined && !isContact(page.step)
            ? [{ question: page.question.slice(0, 300), answer: echoFor(page.step, page.answer, ctx).slice(0, 300) }]
            : []
        ),
        current: origin?.kind === 'question' && origin.answer === undefined ? origin.question.slice(0, 300) : null,
      },
      // For the message's PostHog events (lead_form_chat_*): who asked, and from which step.
      analytics: {
        chatSessionId: chatId.current,
        distinctId: ids.distinctId,
        posthogSessionId: ids.sessionId,
        panelId: origin?.kind === 'question' || origin?.kind === 'statement' ? origin.step.panelId : undefined,
        stepKey: origin?.kind === 'question' || origin?.kind === 'statement' ? origin.step.key : undefined,
        stepNumber: origin?.kind === 'question' ? origin.number : undefined,
        formVariant: 'chat',
      },
    };
    // When Luna isn't available (the key or switch is missing, or a request fails), the page points to an education consultant instead.
    const fallback = `Sorry, I couldn't answer that just now. You can keep going, or call ${displayPhone(phone)} to ask an education consultant.`;

    asking.current?.abort();
    const controller = new AbortController();
    asking.current = controller;
    try {
      const response = await fetch(ASK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) throw new Error('ask_failed');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let answer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        answer += decoder.decode(value, { stream: true });
        update({ answer, status: 'streaming' });
      }
      answer = (answer + decoder.decode()).trim();
      update(answer ? { answer, status: 'done' } : { answer: fallback, status: 'error' });
    } catch {
      if (!controller.signal.aborted) update({ answer: fallback, status: 'error' });
    } finally {
      if (asking.current === controller) asking.current = null;
    }
  };

  // Enter on a statement or an answered question's page carries on (outside fields and buttons).
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, button, a, [contenteditable="true"]')) return;
      const page = view[activeRef.current];
      if (page?.kind === 'statement') {
        event.preventDefault();
        handleYes(page.key);
      } else if (page?.kind === 'question' && page.step.statement && ctx) {
        event.preventDefault();
        handleAnswer(page.key, page.step, optionsFor(page.step, ctx)[0]?.value ?? 'continue');
      } else if (page?.kind === 'ask' && page.ask.status !== 'waiting' && page.ask.status !== 'streaming') {
        event.preventDefault();
        handleAskDone(page.ask);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const handleSubmitLead = async (visitorPhone: string): Promise<string | null> => {
    if (!state) return 'Something went wrong. Please try again.';
    const contact: Contact = { name: state.answers['full-name'] ?? '', email: state.answers.email ?? '', phone: visitorPhone };
    // Never submit without a name, as on the standard form (CAC-2508): the backend rejects it and the lead is lost.
    if (!contact.name.trim()) return 'Please enter your name before submitting.';
    const answers = leadAnswersFrom(state, contact, lookup, form);
    if (!answers) return 'A few answers are missing. Please go back and check them.';
    trackLeadFormSubmitAttempted({ stepCount: pages.filter((page) => page.kind === 'question' && !page.step.statement).length });
    // The standard form counts its last panel as complete when it's submitted.
    void import('@/app/utils/web-analytics').then(({ trackFormStepComplete }) => trackFormStepComplete(PHONE_PANEL_ID)).catch(() => {});
    const result = await (async () => {
      eventId.current ??= await leadEventId();
      return submitChatLead(answers, eventId.current, form.subject ?? '');
    })().catch(() => ({ success: false as const, error: 'We could not send your request. Please try again.' }));
    if (!result.success) {
      failures.current += 1;
      return failures.current > 1 ? `${result.error} You can also call ${displayPhone(phone)} and we'll take it from there.` : result.error;
    }
    try {
      window.sessionStorage.removeItem(STATE_KEY);
      window.sessionStorage.removeItem(ASKS_KEY);
      window.sessionStorage.removeItem(YES_KEY);
    } catch {
      // Browser storage is optional.
    }
    window.location.href = result.redirectUrl;
    return null;
  };

  const activeIndex = Math.min(active, last);
  const activePage = view[activeIndex] ?? null;
  const activeQuestion = activePage?.kind === 'question' ? activePage : null;
  const activePanelId = activeQuestion?.step.panelId ?? null;
  // The same panel views as the standard form (GTM lead_form_panel_load).
  const lastPanelView = useRef<string | null>(null);
  useEffect(() => {
    if (!activePanelId || !activeQuestion || lastPanelView.current === activePanelId) return;
    lastPanelView.current = activePanelId;
    void import('@/app/utils/analytics').then(({ trackPanelView }) => trackPanelView(activePanelId)).catch(() => {});
    trackLeadFormStepViewed(leadFormStepFor(activeQuestion));
  }, [activePanelId, activeQuestion]);
  // The contact page has no message box under it, so the whole form fits on the screen.
  // Nor does the partner school page: its only answer is Continue, so the box would only take room.
  const hideDock = Boolean(activeQuestion && (isContact(activeQuestion.step) || activeQuestion.step.kind === 'partner'));
  const thinking = asks.some((ask) => ask.status === 'waiting' || ask.status === 'streaming');

  const renderInput = (page: Extract<Page, { kind: 'question' }>, isActive: boolean) => {
    if (!ctx) return null;
    const { step, answer } = page;
    const labelId = `tpc-h-${page.key}`;
    const options = optionsFor(step, ctx);
    const { panelId } = step;
    const answerWith = (value: Answer) => handleAnswer(page.key, step, value);
    // The same click events as the standard form's panels.
    const trackAnalytics = (track: (analytics: typeof import('@/app/utils/web-analytics')) => void) => void import('@/app/utils/web-analytics').then(track).catch(() => {});
    const pickOption = (value: Answer) => {
      const text = options.find((option) => option.value === value)?.label ?? value;
      trackAnalytics(({ trackFormInteraction }) => trackFormInteraction(panelId, text));
      answerWith(value);
    };
    if (step.statement) {
      // One answer only ("Continue"), so it's the page's blue button, like a reassurance page's Yes.
      return (
        <div className="tpc-actions">
          <button className="tpc-go tpc-yes" type="button" onClick={() => {
              trackAnalytics(({ trackContinueButtonClick }) => trackContinueButtonClick(panelId));
              answerWith(options[0]?.value ?? 'continue');
            }}>
            {options[0]?.label ?? 'Continue'} <Arrow />
          </button>
          <span className="tpc-enter" aria-hidden="true">
            press <b>Enter ↵</b>
          </span>
        </div>
      );
    }
    switch (step.input) {
      case 'choice':
        return <ChoiceInput options={options} active={isActive} labelId={labelId} selected={answer} onPick={pickOption} />;
      case 'zip':
        return <ZipInput initial={answer ?? ''} panelId={panelId} onDone={answerWith} />;
      case 'name':
      case 'email':
        return <TextStepInput key={page.key} field={step.input} panelId={panelId} initial={answer ?? ''} onDone={answerWith} />;
      case 'phone':
        return <PhoneSubmitForm labelId={labelId} panelId={panelId} onSubmit={handleSubmitLead} />;
    }
  };

  // A question still waiting for its answer types itself in, chunk by chunk as a model streams (CSS plays it
  // when the page comes into view, so the first question types even before the code has loaded); an
  // answered one is just its text.
  const typesIn = (page: Extract<Page, { kind: 'question' }>) => !page.step.statement && page.answer === undefined && Boolean(page.question);
  const questionHeading = (page: Extract<Page, { kind: 'question' }>) => (
    <h2 className={page.step.statement ? 'tpc-statement-h' : 'tpc-q'} id={`tpc-h-${page.key}`} tabIndex={-1}>
      {typesIn(page) ? (
        <span className="tpc-q-text">
          {streamedChunks(page.question).chunks.map((chunk, i) => (
            <span key={i} className="tpc-tok" style={{ '--d': `${chunk.at}ms` } as CSSProperties}>
              {chunk.text}
            </span>
          ))}
        </span>
      ) : (
        page.question
      )}
    </h2>
  );

  let statements = 0;
  // Each page's art is drawn for the learner (art.ts): its age group from the page and the answers before it.
  const answersSoFar = state?.answers ?? {};
  // The contact pages need the full width.
  const artFor = (step: Step): string | null => (isContact(step) ? null : artForStep(step, form, answersSoFar));
  const statementArtFor = (step: Step): string => sellArt(step, form, answersSoFar);

  const renderPage = (page: ViewPage, index: number) => {
    const isActive = index === activeIndex;
    if (page.kind === 'ask') {
      const { ask } = page;
      const origin = view.find((item) => item.key === ask.after);
      const busy = ask.status === 'waiting' || ask.status === 'streaming';
      return (
        <PageFrame
          key={page.key}
          page={page}
          index={index}
          active={isActive}
          art={null}
          head={
            <>
              <p className="tpc-asked">You asked</p>
              <h2 className="tpc-ask-q" id={`tpc-h-${page.key}`} tabIndex={-1}>
                {ask.question}
              </h2>
            </>
          }
        >
          <div className="tpc-ask-row">
            <div className="tpc-ask-a" aria-live="polite" aria-busy={busy}>
              {ask.answer ? (
                <p>
                  <AskText text={ask.answer} />
                </p>
              ) : (
                <Thinking />
              )}
            </div>
            <AnswerMark busy={busy} />
          </div>
          <div className="tpc-actions">
            <button className="tpc-go" type="button" disabled={busy} onClick={() => handleAskDone(ask)}>
              {origin?.kind === 'question' && !origin.step.statement ? 'Back to the question' : 'Keep going'} <Arrow />
            </button>
          </div>
        </PageFrame>
      );
    }
    if (page.kind === 'loading') {
      return (
        <PageFrame key={page.key} page={page} index={index} active={isActive} art={artFor(page.step)} head={null}>
          <div className="tpc-loading" role="status" aria-live="polite">
            <p className="tpc-line">Finding school districts near you…</p>
            <p className="tpc-think" aria-hidden="true">
              One moment
              <span className="tpc-think-dots">
                <span>.</span>
                <span className="is-second">.</span>
                <span className="is-third">.</span>
              </span>
            </p>
          </div>
        </PageFrame>
      );
    }
    if (page.kind === 'statement') {
      const label = YES_LABELS[statements++ % YES_LABELS.length];
      const [heading, ...rest] = page.lines;
      return (
        <PageFrame
          key={page.key}
          page={page}
          index={index}
          active={isActive}
          art={statementArtFor(page.step)}
          head={heading && 'text' in heading ? <TypedHeading id={`tpc-h-${page.key}`} text={heading.text} active={isActive} /> : null}
        >
          {(heading && 'text' in heading ? rest : page.lines).map((line) => (
            <Line key={line.key} line={line} />
          ))}
          <div className="tpc-actions">
            <button className="tpc-go tpc-yes" type="button" onClick={() => handleYes(page.key)}>
              {label} <Arrow />
            </button>
            <span className="tpc-enter" aria-hidden="true">
              press <b>Enter ↵</b>
            </span>
          </div>
        </PageFrame>
      );
    }
    if (page.step.kind === 'partner') {
      return (
        <PartnerReveal
          key={page.key}
          page={page}
          index={index}
          active={isActive}
          art={artFor(page.step)}
          onContinue={() => ctx && handleAnswer(page.key, page.step, optionsFor(page.step, ctx)[0]?.value ?? 'continue')}
        />
      );
    }
    return (
      <PageFrame
        key={page.key}
        page={page}
        index={index}
        active={isActive}
        art={artFor(page.step)}
        kicker={index === 0 && headline ? <h1 className="tpc-page-kicker">{headline}</h1> : undefined}
        typedMs={typesIn(page) ? streamedChunks(page.question).total : undefined}
        head={
          <>
            {/* A statement-style question leads with its heading, then its card. */}
            {page.step.statement ? questionHeading(page) : null}
            {page.lead.map((line) => (
              <Line key={line.key} line={line} />
            ))}
            {page.step.statement ? null : questionHeading(page)}
          </>
        }
      >
        <div className="tpc-answer">{renderInput(page, isActive)}</div>
      </PageFrame>
    );
  };

  return (
    <div className="tpc tpc-paged">
      <div className="tpc-stage" ref={stageRef} aria-label="Questions">
        {view.map(renderPage)}
      </div>

      <div className="tpc-dock-bar" hidden={hideDock}>
        <div className="tpc-dock-in">
          <ComposerBox
            key={activeQuestion?.key ?? 'none'}
            options={activeQuestion && ctx && !isContact(activeQuestion.step) ? optionsFor(activeQuestion.step, ctx) : []}
            disabled={!state}
            busy={thinking}
            onAnswer={(value, typed) => activeQuestion && handleAnswer(activeQuestion.key, activeQuestion.step, value, 'free_text', typed)}
            onAsk={(text) => void handleAsk(text)}
          />
        </div>
      </div>

      <ChatFooter copyrightYear={copyrightYear} />
    </div>
  );
}
